import React from "react";

import { UseOnClickArgs } from "../../hooks/useOnClickProps.js";

import { HexTabId } from "./HexTab.js";

export interface HexTabLinkProps extends Omit<
  React.AnchorHTMLAttributes<HTMLAnchorElement>,
  "id" | "title" | "onClick"
> {
  /**
   * Whether the tab is disabled.
   * @default false
   */
  disabled?: boolean;
  /**
   * Unique identifier used to control which tab is selected
   * and to generate ARIA attributes for accessibility.
   */
  id: HexTabId;
  /**
   * Can be a string or partial `Path` object (pathname, search, and hash
   * values of a URL).
   */
  to: UseOnClickArgs["to"];
  /**
   * Where to open the linked document.
   */
  target?: UseOnClickArgs["target"];
  /**
   * Whether to replace the current entry in the History stack instead of
   * pushing a new one onto it.
   */
  replace?: UseOnClickArgs["replace"];
}

/**
 * An individual interactive tab button that toggles the corresponding panel
 * with the same `id` and navigates to the specified URL when clicked.
 *
 * Important:
 *
 * - Must be a direct child of `HexTabs` (e.g. `<HexTabs><HexTab /></HexTabs>`)
 * - Must NOT be wrapped in a component (e.g. NOT `const MyTab = () => <HexTab />`)
 * - Must NOT be wrapped in a `styled()` (e.g. NOT `const MyTab = styled(HexTab)`)
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- props are handled in `InternalHexTabLink`
export function HexTabLink(props: HexTabLinkProps): null {
  // rendering is delegated to `InternalHexTabLink`
  return null;
}

HexTabLink.displayName = "HexTabLink";
